<template>
  <v-card flat>
    <v-row align="center" class="mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <autocomplete
          :label="$t('meal_categories')"
          v-model="selectedCategories"
          :items="categoryItems"
          item-text="text"
          item-value="value"
          multiple
          hide-details
        />
      </v-col>
    </v-row>
    <v-card-text class="pa-0">
      <grouped-shift-meals-table
        ref="mealsTable"
        :loading="loading"
        :daily-data="dailyData"
        :meal-categories="mealCategories"
        :category-labels="categoryLabels"
        :selected-shift="selectedShifts"
        :selected-categories="selectedCategories"
        :date-array="dateArray"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import GroupedShiftMealsTable from "@/components/report/shared/GroupedShiftMealsTable.vue";

export default {
  name: "CompanyReportTabGroupedShiftMeals",
  mixins: [hasPrinting, hasReportPrinting, hasExcel],
  components: {
    GroupedShiftMealsTable,
  },
  data() {
    return {
      rows: [], // Redovi iz food-korisnici-by-day?grouped=1 (dan, smena, grupa)
      loading: false,
      lastRequestId: 0, // Ignoriši zastarele odgovore pri brzoj promeni filtera
      selectedShifts: [1, 2, 3, 4], // Po default sve četiri smene
      selectedCategories: [], // Filter za grupe jela (category_id)
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    generateDateArray(startDate, endDate) {
      const dates = [];
      const start = new Date(startDate);
      const end = new Date(endDate);

      while (start <= end) {
        dates.push(start.toISOString().substr(0, 10));
        start.setDate(start.getDate() + 1);
      }

      return dates;
    },
    loadData() {
      const companyIds = this.selectedFoodCompanies.map((c) => c.id).join(",");
      const { from, to } = this.filters.dates;
      const requestId = ++this.lastRequestId;

      if (!companyIds || !from || !to || new Date(to) < new Date(from)) {
        this.rows = [];
        this.loading = false;
        return;
      }

      this.loading = true;
      reportService
        .getCompanyFoodKorisniciByDay(companyIds, {
          date: from,
          date2: to,
          grouped: 1,
        })
        .then((response) => {
          if (requestId !== this.lastRequestId) return;
          this.rows = response.data || [];
        })
        .catch(() => {
          if (requestId !== this.lastRequestId) return;
          this.rows = [];
        })
        .finally(() => {
          if (requestId === this.lastRequestId) this.loading = false;
        });
    },
  },
  computed: {
    // Grupe jela po category_id, redosled po category_order
    categoryList() {
      const byId = new Map();
      this.rows.forEach((row) => {
        if (!byId.has(row.category_id)) {
          byId.set(row.category_id, {
            id: row.category_id,
            name: row.category_name,
            order: row.category_order,
          });
        }
      });
      return [...byId.values()].sort((a, b) => a.order - b.order || a.id - b.id);
    },
    mealCategories() {
      return this.categoryList.map((category) => category.id);
    },
    categoryLabels() {
      return this.categoryList.reduce((acc, category) => {
        acc[category.id] = this.toLatin(category.name || "");
        return acc;
      }, {});
    },
    categoryItems() {
      return this.categoryList.map((category) => ({
        text: this.categoryLabels[category.id],
        value: category.id,
      }));
    },
    // { [datum]: { [`${category_id}_${smena}`]: količina } } - samo raspoređivanje, bez sabiranja
    dailyData() {
      const data = {};
      this.dateArray.forEach((date) => {
        data[date] = {};
      });
      this.rows.forEach((row) => {
        if (!data[row.date]) return;
        data[row.date][`${row.category_id}_${row.shift}`] = row.quantity;
      });
      return data;
    },
    selectedFoodCompanies() {
      return this.$store.state.report.selectedFoodCompanies;
    },
    selectedCompanies() {
      return this.selectedFoodCompanies
        .map((company) => this.toLatin(company?.name || ""))
        .join(", ");
    },
    dateArray() {
      if (!this.filters.dates.from || !this.filters.dates.to) return [];

      return this.generateDateArray(this.filters.dates.from, this.filters.dates.to);
    },
    filters() {
      return this.$store.state.report.filters;
    },
  },
  watch: {
    selectedFoodCompanies: {
      handler() {
        this.loadData();
      },
      deep: true,
    },
    "filters.dates": {
      handler() {
        this.loadData();
      },
      deep: true,
    },
    mealCategories: {
      handler(newCategories) {
        // Lista grupa se menja samo novim učitavanjem - selektuj sve
        this.selectedCategories = [...newCategories];
      },
      immediate: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        this.printDocument({
          tables: [
            {
              headers: this.$refs.mealsTable.printHeaders,
              items: this.$refs.mealsTable.printItems,
            },
          ],
          title: [this.$t("report"), "Grupa jela"].join(" "),
          subtitle: this.subtitle,
          beforeTableHtml: `<h4 style="text-align: center">${this.selectedCompanies}</h4>`,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: [this.$t("report"), "Grupa jela"].join(" ") + ".xlsx",
          sheets: [
            {
              name: "Grupa jela",
              headers: this.$refs.mealsTable.headers,
              items: this.$refs.mealsTable.printItems,
              title: [this.$t("Repreportrt"), "Grupa jela"].join(" "),
              subtitle: this.subtitle,
              beforeTableHtml: `<h4 style="text-align: center">${this.selectedCompanies}</h4>`,
            },
          ],
        });
      }
    },
  },
};
</script>
